import { useMemo } from "react"
import { useIntl } from "react-intl"

import dayjs from "dayjs"

import { StaffBodyTextXXS } from "@doktor-se/bones-ui"

import { appLanguages } from "config"
import { useAppSelector } from "lib/hooks"
import { patientProfilesSelectors } from "reducers/users/users.reducer"

import styles from "./SlotHistoryEventDetails.module.scss"

const ProfileDetails = ({ key, eventData }: { key: string; eventData: { [key: string]: any } }) => {
  const intl = useIntl()
  const patientProfiles = useAppSelector(state =>
    patientProfilesSelectors.selectById(state, eventData.clientId!)
  )?.profiles
  const profile = patientProfiles?.find(p => p.id === eventData.profileId)

  const profileName = useMemo(() => {
    if (!profile) return ""
    if (profile.data.securityNumber) {
      return `${profile.name} (${profile.data.securityNumber})`
    }
    return `${profile.name}`
  }, [profile])

  // in case we wont find a profile (previous bookings and so on we just dont display it here)
  if (!profile) return null

  return (
    <div key={key}>
      <StaffBodyTextXXS>{intl.formatMessage({ id: `profile.type.${eventData.profileType}` })}</StaffBodyTextXXS>
      <StaffBodyTextXXS>{profileName}</StaffBodyTextXXS>
    </div>
  )
}

const SlotHistoryEventDetails = ({ eventData }: { eventData: { [key: string]: any } }) => {
  const employeeProfiles = useAppSelector(state => state.users.staff)
  const types = useAppSelector(state => state.booking.bookingTypes)
  const intl = useIntl()

  const renderTimeMessage = ({ key, messageId, data }: { data: any; messageId: string; key: string }) => {
    return (
      <div key={key}>
        <StaffBodyTextXXS>{intl.formatMessage({ id: messageId })}</StaffBodyTextXXS>
        <StaffBodyTextXXS>{`${dayjs(data.start).format("YYYY-MM-DD HH:mm")} - ${dayjs(data.end).format(
          "HH:mm"
        )}`}</StaffBodyTextXXS>
      </div>
    )
  }

  const renderStaffMessage = ({ key, data, messageId }: { key: string; data: any; messageId: string }) => {
    const staff = employeeProfiles?.find(staff => staff.id === data)
    const staffDisplayName = staff
      ? staff.displayName
      : intl.formatMessage({ id: "booking.history.event.details.unassigned" })

    return (
      <div key={key}>
        <StaffBodyTextXXS>{intl.formatMessage({ id: messageId })}</StaffBodyTextXXS>
        <StaffBodyTextXXS>{staffDisplayName}</StaffBodyTextXXS>
      </div>
    )
  }

  const renderKindMessage = ({ key, data, messageId }: { key: string; data: any; messageId: string }) => {
    const localeCode = appLanguages.find(language => language.locale === intl.locale)?.code

    const kindId = Array.isArray(data) ? data[0] : data

    const kindName = types?.find(kind => kind.id === kindId)?.localisedNames[localeCode || intl.defaultLocale]

    return (
      <div key={key}>
        <StaffBodyTextXXS>{intl.formatMessage({ id: messageId })}</StaffBodyTextXXS>
        <StaffBodyTextXXS>{kindName}</StaffBodyTextXXS>
      </div>
    )
  }

  return (
    <div className={styles.eventDetails}>
      {Object.entries(eventData).map(([key, data], idx) => {
        switch (key) {
          case "prevBookingTime":
            return renderTimeMessage({
              key: `${idx}-prev-booking-time`,
              messageId: "booking.history.event.details.prev_booking_time",
              data
            })
          case "newBookingTime":
            return renderTimeMessage({
              key: `${idx}-new-booking-time`,
              messageId: "booking.history.event.details.new_booking_time",
              data
            })
          case "bookingTime":
            return renderTimeMessage({
              key: `${idx}-booking-time`,
              messageId: "booking.history.event.details.booking_time",
              data
            })
          case "slotTime":
            return renderTimeMessage({
              key: `${idx}-slot-time`,
              messageId: "booking.history.event.details.slot_time",
              data
            })
          case "prevStaffId":
            return renderStaffMessage({
              key: `${idx}-prev-staff-name`,
              messageId: "booking.history.event.details.prev_staff_name",
              data
            })
          case "newStaffId":
            return renderStaffMessage({
              key: `${idx}-new-staff-name`,
              messageId: "booking.history.event.details.new_staff_name",
              data
            })
          case "staffId":
            return renderStaffMessage({
              key: `${idx}-staff-name`,
              messageId: "booking.history.event.details.staff_name",
              data
            })
          case "kindIds":
          case "kinds":
            return renderKindMessage({
              key: `${idx}-kind-name`,
              messageId: "booking.history.event.details.kind_name",
              data
            })
          case "profileId":
            return <ProfileDetails key={`${idx}-profile-details`} eventData={eventData} />
          default:
            return null
        }
      })}
    </div>
  )
}

export default SlotHistoryEventDetails
